// src/components/EnrollStudyPickerModal.js
//
// Shown when a user marks a patient Enrolled (or clicks "Add study"): pick the
// study, or studies, the patient is enrolled in. Only ACTIVE and ACTIVE/NR
// studies are offered, and typing filters the list.
//
// The pop-up works out the outcome live using the shared rule in
// enrollmentRules.js, and tells the user before they confirm:
//   - every study is diagnostics (or closed): back to Lung Center Referrals
//   - anything else: the patient stays Enrolled
// Nothing is saved here. The parent applies the result to its state, and the
// normal Save writes it, exactly as every other edit on the patient page.

import { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Stethoscope, X, ArrowRightLeft, CheckCircle2 } from "lucide-react";
import SearchableSelect from "./SearchableSelect";
import {
  isEnrollableStudy,
  isDiagnosticsStudy,
  shouldReturnToReferrals,
  unverifiedLowMatches,
  studyDisplayName,
  RETURN_REASON_TEXT,
  REFERRALS_STATUS,
} from "../services/enrollmentRules";

const EnrollStudyPickerModal = ({
  open,
  studies = [],
  studiesById,
  currentEntries = [],
  // Optional: studies ticked when it opens (the weekly report's "Add to RegX"
  // pre-ticks the report's study). The patient page passes nothing: empty.
  initialPicks,
  onCancel,
  onConfirm,
}) => {
  const [picks, setPicks] = useState([]);
  const initialRef = useRef(initialPicks);
  initialRef.current = initialPicks;

  useEffect(() => {
    if (open) setPicks((initialRef.current || []).filter((s) => s && isEnrollableStudy(s)));
  }, [open]);

  const takenIds = useMemo(
    () => new Set([...currentEntries.map((e) => String(e.studyId)), ...picks.map((s) => String(s.id))]),
    [currentEntries, picks]
  );

  // Unique label per study. Two studies can share a NAME, so a repeated name
  // gets its STUDY_ID (or doc id) appended to tell them apart.
  const labelToStudy = useMemo(() => {
    const enrollable = studies.filter((s) => isEnrollableStudy(s) && !takenIds.has(String(s.id)));
    const counts = new Map();
    enrollable.forEach((s) => {
      const n = studyDisplayName(s);
      counts.set(n, (counts.get(n) || 0) + 1);
    });
    const map = new Map();
    enrollable
      .slice()
      .sort((a, b) => studyDisplayName(a).localeCompare(studyDisplayName(b)))
      .forEach((s) => {
        const n = studyDisplayName(s);
        const label = counts.get(n) > 1 ? `${n} (${s.STUDY_ID || s.id})` : n;
        map.set(label, s);
      });
    return map;
  }, [studies, takenIds]);

  const options = useMemo(() => Array.from(labelToStudy.keys()), [labelToStudy]);

  const outcome = useMemo(() => {
    const combined = [
      ...currentEntries,
      ...picks.map((s) => ({ studyId: s.id, disease: s.DISEASE || "" })),
    ];
    const result = shouldReturnToReferrals(combined, studiesById);
    // Same guard as every other path: an unverified match under 90% can't be
    // what frees the patient. They stay Enrolled until staff verify it.
    const unsure = unverifiedLowMatches(combined);
    if (result.move && unsure.length) {
      return { move: false, reason: null, keeping: unsure, unsure: true };
    }
    return result;
  }, [currentEntries, picks, studiesById]);

  const canConfirm = picks.length > 0 || currentEntries.length > 0;
  const nameOf = (id) => studyDisplayName(studiesById?.get?.(id)) || id;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="enroll-study-picker"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
        >
          <motion.div
            initial={{ scale: 0.94, opacity: 0, y: 8 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.94, opacity: 0, y: 8 }}
            transition={{ type: "spring", stiffness: 300, damping: 24 }}
            className="enroll-modal-panel relative w-full max-w-md rounded-2xl border border-emerald-400/40 bg-slate-950/95 backdrop-blur-2xl shadow-[0_24px_60px_-12px_rgba(52,211,153,0.5)]"
          >
            <div className="absolute top-0 inset-x-0 h-[2px] bg-gradient-to-r from-emerald-400 to-teal-400 opacity-80 pointer-events-none rounded-t-2xl" />
            <div className="p-5">
              <div className="flex items-start gap-3 mb-4">
                <div className="w-9 h-9 rounded-xl bg-emerald-500/20 border border-emerald-400/30 flex items-center justify-center shrink-0">
                  <Stethoscope className="w-[18px] h-[18px] text-emerald-300" />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-white leading-tight">
                    Which study is this patient enrolled in?
                  </h3>
                  <p className="text-xs text-slate-300 mt-1 leading-relaxed">
                    Type to search active studies. A patient can be in more than one, so add each study they're
                    enrolled in.
                  </p>
                </div>
              </div>

              {currentEntries.length > 0 && (
                <div className="mb-3">
                  <p className="text-[11px] uppercase tracking-wide text-slate-500 mb-1.5">Already enrolled in</p>
                  <div className="flex flex-wrap gap-1.5">
                    {currentEntries.map((e) => (
                      <span
                        key={`cur-${e.studyId}`}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-white/10 bg-white/[0.05] px-2.5 py-1 text-xs text-slate-200"
                      >
                        {e.studyName || nameOf(e.studyId)}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {picks.length > 0 && (
                <div className="mb-3 flex flex-wrap gap-1.5">
                  {picks.map((s) => (
                    <span
                      key={s.id}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-300/30 bg-emerald-500/15 px-2.5 py-1 text-xs font-medium text-emerald-100"
                    >
                      {studyDisplayName(s)}
                      {isDiagnosticsStudy(s) && (
                        <span className="rounded bg-teal-400/20 px-1 text-[10px] font-semibold text-teal-200">
                          DIAGNOSTICS
                        </span>
                      )}
                      <button
                        type="button"
                        aria-label={`Remove ${studyDisplayName(s)}`}
                        onClick={() => setPicks((p) => p.filter((x) => x.id !== s.id))}
                        className="text-emerald-200 hover:text-white"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}

              <SearchableSelect
                value=""
                options={options}
                onChange={(label) => {
                  const s = labelToStudy.get(label);
                  if (s) setPicks((p) => [...p, s]);
                }}
                placeholder={picks.length ? "Add another study…" : "Type a study name…"}
                noMatchText="No active study matches."
                emptyText="No other active studies."
                optionHint={(label) => (isDiagnosticsStudy(labelToStudy.get(label)) ? " · Diagnostics" : "")}
                optionIcon={Stethoscope}
              />

              {canConfirm && (
                <div
                  className={`mt-3 flex items-start gap-2 rounded-lg border px-3 py-2 text-xs leading-relaxed ${
                    outcome.move
                      ? "border-teal-400/30 bg-teal-400/10 text-teal-100"
                      : "border-white/10 bg-white/[0.04] text-slate-300"
                  }`}
                >
                  {outcome.move ? (
                    <ArrowRightLeft className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                  ) : (
                    <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 shrink-0 text-emerald-300" />
                  )}
                  <span>
                    {outcome.move ? (
                      <>
                        {RETURN_REASON_TEXT[outcome.reason]}, so after you Save this patient moves back to{" "}
                        <span className="font-semibold text-white">{REFERRALS_STATUS}</span> to be considered for
                        other studies. Their enrollment stays on record.
                      </>
                    ) : (
                      <>
                        This patient will stay <span className="font-semibold text-white">Enrolled</span>
                        {outcome.unsure
                          ? ` until the ${outcome.keeping
                              .map((e) => `${e.studyName || nameOf(e.studyId)} match (${e.matchScore}%)`)
                              .join(", ")} is verified on the patient page.`
                          : outcome.keeping.length
                          ? ` (in ${outcome.keeping
                              .map((e) => e.studyName || nameOf(e.studyId))
                              .join(", ")}).`
                          : "."}
                      </>
                    )}
                  </span>
                </div>
              )}

              <p className="mt-2 text-[11px] text-slate-500">
                New enrollments are sent to an admin to approve onto the study's enrolled list.
              </p>

              <div className="mt-4 grid grid-cols-2 gap-2">
                <button
                  type="button"
                  disabled={!canConfirm}
                  onClick={() => onConfirm(picks, outcome)}
                  className="enroll-solid-btn inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 ring-1 ring-white/20 text-sm font-semibold text-white hover:shadow-[0_8px_24px_-6px_rgba(52,211,153,0.6)] active:scale-[0.98] transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Confirm
                </button>
                <button
                  type="button"
                  onClick={onCancel}
                  className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-white/[0.06] border border-white/15 text-sm font-semibold text-slate-100 hover:bg-white/[0.1] active:scale-[0.98] transition-all"
                >
                  Cancel
                </button>
              </div>
              {!canConfirm && (
                <p className="mt-2 text-center text-[11px] text-slate-500">
                  Pick at least one study to mark this patient Enrolled.
                </p>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default EnrollStudyPickerModal;
